import { styled } from '@mui/material/styles';
import { Paper, Typography, Box, Tooltip, IconButton } from '@mui/material';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import EventIcon from '@mui/icons-material/Event';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import EditIcon from '@mui/icons-material/Edit';
import WcOutlinedIcon from '@mui/icons-material/WcOutlined';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { Passenger } from '../../../data/models/passenger.model';
import { useAddress } from '../../../hooks/display/useAddress';
import { useDate } from '../../../hooks/display/useDate';
import { usePassengerPhone } from '../../../hooks/display/usePassengerPhone';
import { useGender } from '../../../hooks/display/useGender';
import { ServicePlan } from '../../../data/models/service-plan.model';
import PassengerBenefits from './PassengerBenefits';
import PassengerNamedLocations from './PassengerNamedLocations';
import ProfileRow from './ProfileRow';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';
import { useFeatureMemberPreferences } from '../../../hooks/features/useFeatureMemberPreferences';
import PassengerPreferences from './PassengerPreferences';
import { usePortalName } from '../../../hooks/usePortalName';
import { EDIT_MEMBER_PROFILE_NOT_ALLOWED_PORTAL_NAMES } from '../../../data/constants';
import * as passengerSlice from '../../../data/passenger.slice';
import { useEffect } from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';

const PREFIX = 'PassengerProfile';

const classes = {
  container: `${PREFIX}-container`,
  details: `${PREFIX}-details`,
  header: `${PREFIX}-header`,
  benefits: `${PREFIX}-benefits`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    minWidth: 444,
  },

  [`& .${classes.details}`]: {
    padding: theme.spacing(3, 4),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },

  [`& .${classes.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    minHeight: 40,
  },

  [`& .${classes.benefits}`]: {
    marginTop: theme.spacing(2),
  },
}));

interface PassengerProfileProps {
  passenger: Passenger | null;
  servicePlans: ServicePlan[];
  edit: (passenger: Passenger) => void;
  readonly: boolean;
}

export default function PassengerProfile({
  passenger,
  servicePlans,
  edit,
  readonly,
}: PassengerProfileProps) {
  const t = useCommonTranslation();
  const portalName = usePortalName() || '';
  const dispatch = useAppDispatch();

  const isMemberPreferencesEnabled = useFeatureMemberPreferences();
  const isSuperReaderUser = useIsSuperReaderUser();

  const landlinePhone = usePassengerPhone(
    passenger?.passengerPhones,
    'LANDLINE',
  );
  const naPhone = usePassengerPhone(passenger?.passengerPhones, 'NA');
  const passengerPreferences = useAppSelector(
    passengerSlice.selectCurrentPassengerPreferences,
  );

  useEffect(() => {
    if (passenger) {
      dispatch(
        passengerSlice.fetchPassengerPreferencesAsync(passenger.passengerUUID!),
      );
    }
  }, [passenger]);

  return (
    <StyledBox
      className={classes.container}
      data-testid="passenger-profile-container">
      <Paper
        elevation={2}
        className={classes.details}
        data-testid="passenger-profile-section">
        <Box className={classes.header} data-testid="passenger-profile-header">
          <Typography variant="h3">{t('passenger.profile')}</Typography>
          {!readonly &&
            !!passenger?.manual &&
            !isSuperReaderUser &&
            !EDIT_MEMBER_PROFILE_NOT_ALLOWED_PORTAL_NAMES.includes(
              portalName,
            ) && (
              <Tooltip title={t('passenger.editProfile')}>
                <span>
                  <IconButton
                    data-testid="edit-profile"
                    onClick={(evt) => edit(passenger!)}
                    color="primary"
                    disabled={!passenger}>
                    <EditIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
        </Box>
        <ProfileRow
          label={t('passenger.clientPassengerId')}
          value={passenger?.clientPassengerID}
          icon={PersonOutlineOutlinedIcon}
        />
        <ProfileRow
          label={t('passenger.dob')}
          value={useDate(passenger?.dateOfBirth)}
          icon={EventIcon}
        />
        <ProfileRow
          label={t('passenger.gender')}
          value={useGender(passenger?.genderCode)}
          icon={WcOutlinedIcon}
        />
        <ProfileRow
          label={t('passenger.address')}
          value={
            passenger?.passengerAddresses?.find(
              (addr) => addr.passengerAddressUseBC === 'MANUAL',
            )
              ? useAddress(
                  passenger?.passengerAddresses?.find(
                    (addr) => addr.passengerAddressUseBC === 'MANUAL',
                  ),
                )
              : useAddress(passenger?.passengerAddresses?.[0])
          }
          icon={PlaceOutlinedIcon}
        />
        <ProfileRow
          label={t('passenger.phoneNumber')}
          value={landlinePhone || naPhone}
          icon={CallOutlinedIcon}
        />
        <ProfileRow
          label={t('passenger.mobile')}
          value={usePassengerPhone(passenger?.passengerPhones, 'MOBILE')}
          icon={PhoneIphoneRoundedIcon}
        />
        <ProfileRow
          label={t('passenger.email')}
          value={passenger?.passengerEmailAddress || undefined}
          icon={MailOutlineRoundedIcon}
        />
      </Paper>
      {isMemberPreferencesEnabled && (
        <PassengerPreferences
          passengerUUID={passenger?.passengerUUID!}
          passengerPreferences={passengerPreferences}
        />
      )}
      <PassengerBenefits servicePlans={servicePlans} passenger={passenger} />
      <PassengerNamedLocations passenger={passenger} />
    </StyledBox>
  );
}
